import { RouteProp, useRoute } from '@react-navigation/native';
import React, { useCallback } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  AuthBackButton,
  AuthPrimaryButton,
  AuthScreenBackground,
} from '@/features/auth/components';
import { createStyles, useScaling, useUnAuthStackNavigation } from '@/hooks';
import type { UnAuthStackParamList } from '@/navigations/types';
import { colors, guardianColors } from '@/theme/colors';

/** Concentric rings around the success check, from outermost to innermost (matches Figma). */
const RING_OUTER = 'rgba(0, 55, 149, 0.04)';
const RING_MIDDLE = 'rgba(0, 55, 149, 0.08)';
const RING_INNER = 'rgba(0, 55, 149, 0.12)';

export function VerifyEmailLinkSentScreen() {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const navigation = useUnAuthStackNavigation();

  const { email } = useRoute<RouteProp<UnAuthStackParamList, 'VerifyEmailLinkSent'>>().params;

  const strings = STRINGS.auth.verifyEmailLinkSent;

  const top = insets.top > 0 ? insets.top + scale(16) : scale(50);

  const handleBackToLogin = useCallback(() => {
    // If Login is already in the stack, pop straight back to it. Otherwise fall
    // back to resetting to Welcome and pushing Login.
    const hasLoginInStack = navigation.getState().routes.some(route => route.name === 'Login');
    if (hasLoginInStack) {
      navigation.popTo('Login');
      return;
    }
    navigation.popTo('Welcome');
    navigation.navigate('Login');
  }, [navigation]);

  return (
    <AuthScreenBackground>
      {/* Back */}
      <Stack width={isTablet ? '100%' : scale(335)}>
        <Stack
          left={isTablet ? scale(50) : undefined}
          paddingTop={top}
          style={isTablet ? styles.backButtonTabletContainer : styles.backButtonContainer}>
          <AuthBackButton onPress={navigation.goBack} />
        </Stack>
      </Stack>

      <Stack
        flex={1}
        width={scale({ phone: 335, tablet: 380 })}
        style={{
          paddingTop: isTablet ? top + scale(18) : undefined,
          paddingBottom: insets.bottom + scale({ phone: 12, tablet: 60 }),
        }}>
        {/* Centered success content */}
        <Stack flex={1} align="center">
          {/* Success check with concentric rings */}
          <Stack
            align="center"
            justify="center"
            padding={scale(16)}
            marginTop={scale({ phone: 64, tablet: 70 }) + (isTablet ? top : 0)}
            borderRadius={scale(100)}
            backgroundColor={RING_OUTER}>
            <Stack
              align="center"
              justify="center"
              padding={scale(12)}
              borderRadius={scale(100)}
              backgroundColor={RING_MIDDLE}>
              <Stack
                align="center"
                justify="center"
                padding={scale(10)}
                borderRadius={scale(100)}
                backgroundColor={RING_INNER}>
                <SvgIcon
                  name="IcCheckYourInbox"
                  size={scale(44)}
                  color={guardianColors['Main/Main']}
                />
              </Stack>
            </Stack>
          </Stack>

          {/* Title */}
          <Text
            variant="Font Guardian App/Heading/Heading H6 18px"
            style={styles.title}
            accessibilityRole="header">
            {strings.title}
          </Text>

          {/* Description */}
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.description}>
            {strings.descriptionPrefix}
            {'\n'}
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              fontWeight="700"
              style={styles.email}>
              {email}
            </Text>
            {'\n'}
            {strings.descriptionFollow}
          </Text>
        </Stack>

        {/* Back to login */}
        <AuthPrimaryButton onPress={handleBackToLogin}>
          {strings.buttonBackToLogin}
        </AuthPrimaryButton>
      </Stack>
    </AuthScreenBackground>
  );
}

const useStyles = createStyles(({ scale }) => ({
  backButtonContainer: {
    alignSelf: 'flex-start',
  },
  backButtonTabletContainer: {
    position: 'absolute',
  },
  title: {
    marginTop: scale(40),
    color: colors['10'],
    textAlign: 'center',
  },
  description: {
    marginTop: scale(24),
    color: colors['10'],
    textAlign: 'center',
  },
  email: {
    color: guardianColors['Main/Main'],
  },
}));

export default VerifyEmailLinkSentScreen;
